import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import ImageView from '../../../components/ImageView';
import { LotData } from './AuctionDetailPopup';
import {
  TopSection,
  ArtworkInfo,
  ArtworkContent,
  ArtworkDetails,
  LotHeader,
  ImageAndNameColumn,
  LotNameLabel,
  LotNameValue,
  AuctionEndLabel,
  CountdownTimer,
  ImageAndNameRow,
  LotChip,
  ArtworkTitle,
  BidAmountInfo,
  SectionTitle,
  PriceSection,
  PriceRow,
  PriceColumn,
  PriceLabel,
  CurrentPriceValue,
  StartPriceValue
} from './AuctionDetailStyles';

interface CurrentLotInfoProps {
  currentLot: LotData;
  auctionEndAt?: string; // 경매 종료 시간 추가
  countdownStatus?: {
    isRunning: boolean;
    isPaused: boolean;
    remainingTime: number;
    lotIndex?: number;
  };
}

// 이미지 스와이퍼 컨테이너 (CMS용)
const ImageSwiperContainer = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  
  .swiper {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
    overflow: hidden !important;
  }
  
  .swiper-wrapper {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
  }
  
  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    min-width: 100% !important;
    max-width: 100% !important;
    user-select: none; /* 이미지 선택 방지 */
    
    /* ImageView 내부 이미지 선택 방지 */
    img {
      user-select: none !important;
      -webkit-user-select: none !important;
      -webkit-touch-callout: none !important;
      pointer-events: none !important;
      -webkit-user-drag: none !important;
      -khtml-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
    }
  }
  
  .swiper-pagination {
    bottom: 8px !important;
    z-index: 10;
    
    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: #ccc !important;
      opacity: 1 !important;
      margin: 0 3px !important;
      border: 1px solid white !important;
    }
    
    .swiper-pagination-bullet-active {
      background: #007bff !important;
      border: 1px solid white !important;
    }
  }
`;

const CurrentLotInfo: React.FC<CurrentLotInfoProps> = ({ currentLot, auctionEndAt, countdownStatus }) => {
  // 실시간 시간 업데이트를 위한 state
  const [currentTime, setCurrentTime] = useState(Date.now());

  // 1초마다 현재 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 이미지 URL 처리 함수 (LiveSection과 동일)
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // LOT의 모든 이미지 URL을 가져오는 함수
  const getAllImageUrls = (lot: LotData): string[] => {
    const imageUrls: string[] = [];
    
    // currentLot에서 images 배열이 있는지 확인 (소켓 데이터에서 온 경우)
    if ((lot as any).images && Array.isArray((lot as any).images)) {
      (lot as any).images.forEach((imagePath: string) => {
        if (imagePath && imagePath.trim() !== "") {
          imageUrls.push(getProfileImageSrc(imagePath));
        }
      });
    }
    // thumbnail이 있는 경우 (기존 데이터 구조)
    else if (lot.thumbnail && lot.thumbnail.trim() !== "") {
      imageUrls.push(getProfileImageSrc(lot.thumbnail));
    }
    
    return imageUrls;
  };

  // 첫 번째 이미지 또는 기본 이미지를 가져오는 함수
  const getFirstImageUrl = (lot: LotData): string => {
    const imageUrls = getAllImageUrls(lot);
    return imageUrls.length > 0 ? imageUrls[0] : "/common/nodata.svg";
  };
  // 가격 포맷팅 함수
  const formatPrice = (price: number, currency: 'KRW' | 'USD') => {
    if (currency === 'KRW') {
      return `₩${price.toLocaleString()}`;
    } else {
      return `$${price.toLocaleString()}`;
    }
  };

  // 경매 종료까지 남은 시간 계산 함수 (실시간 업데이트)
  const formatRemainingTime = (auctionEndAt?: string) => {
    if (!auctionEndAt) return '';
    
    try {
      const endTime = new Date(auctionEndAt).getTime();
      const diff = endTime - currentTime;
      
      if (diff <= 0) return '종료됨';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <TopSection>
      <ArtworkInfo>
        {/* LOT 번호와 이름을 가로로 배치 (작품정보 제목 대신) */}
        <LotHeader>
          <LotChip>{currentLot.lotId}</LotChip>
          <ArtworkTitle>{currentLot.lotName || '-'}</ArtworkTitle>
        </LotHeader>
        
        <ArtworkContent>
          <ArtworkDetails>
            {/* 이미지와 LOT 이름을 가로로 배치 */}
            <ImageAndNameRow>
              <ImageSwiperContainer
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {(() => {
                  const imageUrls = getAllImageUrls(currentLot);
                  if (imageUrls.length <= 1) {
                    // 이미지가 1개 이하면 기본 ImageView 사용
                    return (
                      <ImageView
                        imageUrl={getFirstImageUrl(currentLot)}
                        width={120}
                        height={120}
                        borderRadius={8}
                        alt={currentLot.lotName}
                        objectFit="cover"
                      />
                    );
                  } else {
                    // 이미지가 2개 이상이면 스와이퍼 사용
                    return (
                      <Swiper
                        modules={[Pagination]}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        spaceBetween={0}
                        pagination={{ clickable: true }}
                        loop={imageUrls.length > 1}
                        centeredSlides={true}
                        allowTouchMove={true}
                        touchStartPreventDefault={false}
                        grabCursor={true}
                        style={{ 
                          width: '120px', 
                          height: '120px',
                          maxWidth: '120px',
                          maxHeight: '120px'
                        }}
                      >
                        {imageUrls.map((imageUrl, index) => (
                          <SwiperSlide key={index} style={{ 
                            width: '120px !important', 
                            height: '120px !important',
                            maxWidth: '120px',
                            maxHeight: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <ImageView
                              imageUrl={imageUrl}
                              width={120}
                              height={120}
                              borderRadius={8}
                              alt={`${currentLot.lotName} ${index + 1}`}
                              objectFit="cover"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    );
                  }
                })()}
              </ImageSwiperContainer>
              <ImageAndNameColumn>
                <LotNameLabel>LOT명</LotNameLabel>
                <LotNameValue>{currentLot.lotName || '-'}</LotNameValue>
                <AuctionEndLabel>
                  {countdownStatus?.isRunning ? '남은 시간' : '경매종료까지'}
                </AuctionEndLabel>
                <CountdownTimer>
                  {countdownStatus?.isRunning 
                    ? `${countdownStatus.remainingTime}초` 
                    : formatRemainingTime(auctionEndAt)
                  }
                  {countdownStatus?.isPaused && ' (일시정지)'}
                </CountdownTimer>
              </ImageAndNameColumn>
            </ImageAndNameRow>
          </ArtworkDetails>
        </ArtworkContent>
      </ArtworkInfo>
      
      <BidAmountInfo>
        <SectionTitle style={{ marginBottom: '16px' }}>입찰 금액 정보</SectionTitle>
        <PriceSection>
          <PriceRow>
            <PriceColumn>
              <PriceLabel>현재가(KRW)</PriceLabel>
              <CurrentPriceValue>{formatPrice(currentLot.currentPriceKrw, 'KRW')}</CurrentPriceValue>
            </PriceColumn>
            <PriceColumn>
              <PriceLabel>현재가(USD)</PriceLabel>
              <CurrentPriceValue>{formatPrice(currentLot.currentPriceUsd, 'USD')}</CurrentPriceValue>
            </PriceColumn>
          </PriceRow>
        </PriceSection>
        
        <PriceSection>
          <PriceRow>
            <PriceColumn>
              <PriceLabel>시작가(KRW)</PriceLabel>
              <StartPriceValue>{formatPrice(currentLot.startPriceKrw, 'KRW')}</StartPriceValue>
            </PriceColumn>
            <PriceColumn>
              <PriceLabel>시작가(USD)</PriceLabel>
              <StartPriceValue>{formatPrice(currentLot.startPriceUsd, 'USD')}</StartPriceValue>
            </PriceColumn>
          </PriceRow>
        </PriceSection>
      </BidAmountInfo>
    </TopSection>
  );
};

export default CurrentLotInfo;
